import React from 'react'

import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import Flex, { FlexItem } from 'styled-flex-component'

import { IApplicationState } from '@store'
import {
  clearBrandFansErrors,
  createBrandFanGroup,
  IBrandFanEditableFields,
  selectBrandFansErrorCode,
  selectIsCreatingBrandFanGroup,
} from '@store/brandFans'
import { Button, Container, TextField } from '@tribegroup'
import { getMessage } from '@utils'
import ErrorMessage from './ErrorMessage'

interface IProps {
  identityId: number
  influencerId?: number
  submissionId?: number
  campaignId?: number
}

const BrandFansInfluencerIdentityGroupsForm: React.FC<IProps> = ({
  identityId,
  campaignId,
  submissionId,
  influencerId,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const isCreatingBrandFanGroup = useSelector((state: IApplicationState) =>
    selectIsCreatingBrandFanGroup(state),
  )
  const errorCode = useSelector((state: IApplicationState) => selectBrandFansErrorCode(state))
  const hasError = Boolean(errorCode)
  const {
    register,
    getValues,
    trigger,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm({ mode: 'onChange' })

  // react-hook-form v7 returns { name, onChange, onBlur, ref } from register().
  // tribegroup components forward their DOM ref via `innerRef`.
  const nameField = register('name', { required: true })

  React.useEffect(() => {
    const isCreateSuccess = !isCreatingBrandFanGroup && !hasError
    if (isCreateSuccess) {
      reset()
    }
  }, [isCreatingBrandFanGroup])

  const onCreateBrandFanGroup = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (await trigger()) {
      dispatch(
        createBrandFanGroup(
          identityId,
          campaignId,
          submissionId,
          getValues() as IBrandFanEditableFields,
          influencerId,
        ),
      )
    }
  }

  const onClearErrors = () => {
    if (hasError) {
      dispatch(clearBrandFansErrors())
    }
  }
  React.useEffect(() => onClearErrors)

  return (
    <form data-testid="brand-fans-group-create-form">
      <Flex alignCenter>
        <FlexItem grow={1}>
          <Container rightOuterSpacing={0.75}>
            <TextField
              fullWidth
              thick
              autoFocus
              scheme="default"
              name={nameField.name}
              onBlur={nameField.onBlur}
              innerRef={nameField.ref}
              data-testid="brand-fans-group-create-form-input"
              fontSize={0.875}
              defaultValue=""
              maxLength={60}
              onChange={(event) => {
                void nameField.onChange(event)
                onClearErrors()
              }}
              placeholder={getMessage(intl, 'brandFans.identityGroups.input.placeholder')}
            />
          </Container>
        </FlexItem>
        <FlexItem shrink>
          <Button
            scheme="primary"
            data-testid="brand-fans-group-create-form-button"
            disabled={isCreatingBrandFanGroup || !isDirty || !isValid}
            loading={isCreatingBrandFanGroup}
            onClick={onCreateBrandFanGroup}
          >
            <FormattedMessage id="core.text.add" />
          </Button>
        </FlexItem>
      </Flex>
      {errors.name && (
        <ErrorMessage>
          <FormattedMessage id="brandFans.identityGroups.input.error.required" />
        </ErrorMessage>
      )}
      {hasError && (
        <ErrorMessage>
          <FormattedMessage id={`brandFans.error.${errorCode}`} />
        </ErrorMessage>
      )}
    </form>
  )
}

export { BrandFansInfluencerIdentityGroupsForm }
export default BrandFansInfluencerIdentityGroupsForm
